import {useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import useRegistration from '@/containers/ConnectionPages/ConnectionPage/hooks/useRegistration';
import deviceStateService from '@/services/device/deviceStateService';
import mwIotService from '@/services/mw/mwIotService';
import {selectIsDeviceConnectedToIot, selectIsHolderConnectedToIot} from '@/state/selectors/device';
import {
    selectChargerCodentify,
    selectIsDeviceInitialized,
    selectIsHolderInitialized,
    selectMwDeviceType,
} from '@/state/selectors/mw';

import {useConnectionContext} from '../ConnectionContext';
import connectionSteps from './connectionSteps';

/**
 * Hook to determine device type from URL params or state
 */
const useDeviceType = () => {
    const {deviceType: deviceTypeFromUrl} = useParams();
    const deviceTypeFromState = useSelector(selectMwDeviceType);

    return deviceTypeFromState || deviceTypeFromUrl;
};

/**
 * Hook to handle IoT synchronization
 */
const useIotSynchronization = (updateConnectionState) => {
    const isDeviceConnectedToIot = useSelector(selectIsDeviceConnectedToIot);
    const isHolderConnectedToIot = useSelector(selectIsHolderConnectedToIot);
    const isHolderInitialized = useSelector(selectIsHolderInitialized);
    // Skip holder connect if no holder is present or it's already connected
    const shouldSkipHolderConnect = !isHolderInitialized || isHolderConnectedToIot;
    const storeCodentify = useStoreDeviceCodentify();

    useEffect(() => {
        if (shouldSkipHolderConnect && isDeviceConnectedToIot) {
            storeCodentify();
            updateConnectionState({isConnectionFinished: true});
        }
    }, [shouldSkipHolderConnect, isDeviceConnectedToIot]);

    return () => {
        updateConnectionState({currentStep: connectionSteps.IOT_SYNCHRONIZATION});

        mwIotService.getUamDataForConnectedDevice();
    };
};

/**
 * Hook to handle device registration flow
 */
const useDeviceRegistrationFlow = (currentStep) => {
    const isDeviceInitialized = useSelector(selectIsDeviceInitialized);
    const {updateConnectionState} = useConnectionContext();

    const sync = useIotSynchronization(updateConnectionState);

    const {initiateDeviceRegistrationAndSync} = useRegistration({
        updateConnectionState,
        sync,
    });

    useEffect(() => {
        if (!isDeviceInitialized || !connectionSteps.isAtPairingStep(currentStep)) return;

        initiateDeviceRegistrationAndSync();
    }, [isDeviceInitialized, currentStep]);
};

const useStoreDeviceCodentify = () => {
    const chargerCodentify = useSelector(selectChargerCodentify);

    return () => {
        // Set current device as selected device when device is initialized
        deviceStateService.storeSelectedDeviceCodentify(chargerCodentify, true);
    };
};

/**
 * Main hook for handling device connection flow
 */
const useConnectionPage = () => {
    const deviceType = useDeviceType();
    const {connectionState, updateConnectionState} = useConnectionContext();
    const {currentStep, steps, isConnectionFinished, isQsgOpened} = connectionState;

    // Initialize device registration flow
    useDeviceRegistrationFlow(currentStep);

    const handleQsgOpen = (open) => {
        updateConnectionState({isQsgOpened: open});
    };

    return {
        deviceType,
        currentStep,
        steps,
        isConnectionFinished,
        isQsgOpened,
        handleQsgOpen,
    };
};

export default useConnectionPage;
